import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import TagManager from 'react-gtm-module';

import { EntryContext } from '../contexts/entryContext';
import ErrorMessage from '../components/ErrorMessage';
import ERROR_MESSAGES from '../contants/errorConstants';

const Home = () => {
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState(null);
    const [height, setHeight] = useState(window.innerHeight);
    const { setEntry, igrGameUrl, selectedRevealGame, errorCode } = useContext(EntryContext);

    const callToActionURL = selectedRevealGame?.callToActionURL;
    const revealGameTag = selectedRevealGame?.tag ?? '';

    // Optimize iframe height on window resize for mobile devices
    useEffect(() => {
        const handleResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Clear entry data when landing on this page
        setEntry(null);
    }, []);

    useEffect(() => {
        const handleIGRMessages = (event) => {
            // Redirect to CTA URL when player clicked the CTA button in the IGR iframe
            if (event.data && event.data.type === 'enterTicket') {
                if (callToActionURL) {
                    window.location.replace(callToActionURL);
                    return;
                } else {
                    navigate(`/`, { replace: true });
                    return;
                }
            }

            // GA4 event tracking
            if (event?.data && event?.data?.type === 'trackEvent') {
                const eventData = JSON.parse(event?.data?.data) ?? {};
                const eventType = eventData?.[0];
                const eventName = eventData?.[1];
                const eventParams = eventData?.[2] ?? {};

                // Flatten event details and add "game_details_" prefix to keys
                const namespacedParams = Object.entries(eventParams).reduce((acc, [key, value]) => {
                    acc[`game_details_${key}`] = value;
                    return acc;
                }, {});

                if (eventType && eventName) {
                    const tagManagerArgs = {
                        dataLayer: {
                            event: 'GAME_EVENT',
                            game_event_name: `game_${eventName}`,
                            game_name: revealGameTag,
                            game_provider: 'IGR',
                            ...namespacedParams,
                        },
                    };
                    TagManager.dataLayer(tagManagerArgs);
                    //? For debug: console.log('🎯 Event tracking:', tagManagerArgs);
                }
            }

            // Idle timeout - redirect to home page
            if (event.data && event.data.type === 'idleTimeout') {
                navigate(`/`, { replace: true });
                return;
            }
        };

        window.addEventListener('message', handleIGRMessages);

        return () => {
            window.removeEventListener('message', handleIGRMessages);
        };
    }, [callToActionURL, revealGameTag, navigate]);

    useEffect(() => {
        if (!igrGameUrl) {
            //? ERROR_PLAY_PAGE_RELOADED: Play page was reloaded or accessed directly without going through welcome page
            setErrorMessage(ERROR_MESSAGES.PLAY_PAGE_RELOADED);
        }
    }, [igrGameUrl]);

    // Display error message if it exists
    if (errorMessage) {
        return <ErrorMessage errorMessage={errorCode ?? errorMessage} />;
    }

    return (
        <>
            <Helmet>
                <title>Play - Scratch Connect</title>
            </Helmet>
            <iframe
                title='Play Game'
                tabindex='0'
                className={errorCode ?? ''}
                src={igrGameUrl}
                style={{ width: '100%', height: `${height}px`, border: 'none' }}
            />
        </>
    );
};

export default Home;
